
import { useState, useEffect } from "react";
import { ModernProgress } from "@/components/ui/progress/ModernProgress";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshCw, Wifi, WifiOff, AlertTriangle } from "lucide-react";
import { useRobustProgressTracking } from "@/hooks/useRobustProgressTracking";
import { cn } from "@/lib/utils";

interface RobustProgressBarProps {
  collectionId: string;
  onComplete?: () => void;
  className?: string;
  autoHide?: boolean;
  autoHideDelay?: number;
}

export const RobustProgressBar = ({
  collectionId,
  onComplete,
  className,
  autoHide = true,
  autoHideDelay = 5000
}: RobustProgressBarProps) => {
  const [showProgress, setShowProgress] = useState(true);
  const [hasCompleted, setHasCompleted] = useState(false);

  const {
    progress,
    isLoading,
    error,
    lastSync,
    isOffline,
    retryCount,
    hasActiveProcessing,
    forceRefresh
  } = useRobustProgressTracking(collectionId);

  console.log(`📊 RobustProgressBar render - Collection: ${collectionId}`, {
    progress: progress?.status,
    isLoading,
    error,
    isOffline,
    hasActiveProcessing,
    showProgress
  });

  // Handle completion
  useEffect(() => {
    if (progress?.status === 'completed' && !hasCompleted) {
      setHasCompleted(true);
      console.log(`✅ Progress completed for collection: ${collectionId}`);
      
      if (onComplete) {
        onComplete();
      }

      if (autoHide) {
        setTimeout(() => {
          setShowProgress(false);
        }, autoHideDelay);
      }
    }
  }, [progress?.status, hasCompleted, onComplete, autoHide, autoHideDelay, collectionId]);

  // ENHANCED: Don't show if no progress data and not loading
  // Also check if there's actually any need for progress tracking
  if (!progress && !isLoading && !error) {
    return null;
  }

  // CRITICAL FIX: Don't show progress bar when no actual processing is needed
  // This prevents showing progress bars for collections where all products already have descriptions
  if (progress && !hasActiveProcessing && progress.status === 'completed' && 
      progress.processed_items === progress.total_items && progress.total_items > 0) {
    console.log(`📊 Hiding progress bar - collection ${collectionId} is fully completed`);
    return null;
  }

  // Don't show if completed and auto-hide is enabled
  if (!showProgress && hasCompleted && autoHide) {
    return null;
  }

  // Calculate progress values
  const processedItems = Math.max(0, progress?.processed_items || 0);
  const totalItems = Math.max(1, progress?.total_items || 1);
  const isCompleted = progress?.status === 'completed';
  const isProcessing = progress?.status === 'processing' || progress?.status === 'pending';

  // Determine current stage and action
  const getProgressStage = () => {
    if (progress?.current_stage) {
      return progress.current_stage;
    }
    
    if (isCompleted) return 'completed';
    if (isProcessing) return 'processing';
    return 'preparing';
  };

  const getCurrentAction = () => {
    if (progress?.current_action) {
      return progress.current_action;
    }
    
    if (isCompleted) return `Completed processing ${totalItems} products`;
    if (isProcessing) return `Processing ${processedItems}/${totalItems} products`;
    return 'Preparing...';
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Connection Status */}
      {(isOffline || error) && (
        <Alert variant={isOffline ? "default" : "destructive"}>
          <div className="flex items-center gap-2">
            {isOffline ? (
              <WifiOff className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <AlertDescription className="flex-1">
              {isOffline 
                ? "Connection lost. Progress updates may be delayed." 
                : `Progress tracking error: ${error}`}
              {retryCount > 0 && ` (Retry ${retryCount}/5)`}
            </AlertDescription>
            <Button
              size="sm"
              variant="outline"
              onClick={forceRefresh}
              className="flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </Button>
          </div>
        </Alert>
      )}

      {/* Progress Bar */}
      <div className="space-y-2">
        <ModernProgress
          processed={processedItems}
          total={totalItems}
          stage={getProgressStage()}
          status={progress?.status as 'pending' | 'processing' | 'completed' | 'error' || 'pending'}
          error={error}
          currentAction={getCurrentAction()}
          logMessages={Array.isArray(progress?.log_messages) ? progress.log_messages : []}
        />

        {/* Status Information */}
        <div className="flex justify-between items-center text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {isOffline ? (
              <WifiOff className="h-3 w-3 text-orange-500" />
            ) : (
              <Wifi className="h-3 w-3 text-green-500" />
            )}
            <span>
              {lastSync 
                ? `Last updated: ${lastSync.toLocaleTimeString()}`
                : 'Connecting...'}
            </span>
          </div>
          
          {hasActiveProcessing && (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span>Live updates</span>
            </div>
          )}
        </div>
      </div>

      {/* Manual Controls */}
      {(error || isCompleted) && (
        <div className="flex justify-center">
          <Button
            size="sm"
            variant="outline"
            onClick={forceRefresh}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh Status
          </Button>
        </div>
      )}
    </div>
  );
};
